import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import api from '../api/axios';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [shakeBtn, setShakeBtn] = useState(false);

  const rules = useMemo(() => ({
    length: pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  }), [pw]);

  const isPasswordValid = Object.values(rules).every(Boolean);
  const strengthLevel = Object.values(rules).filter(Boolean).length;

  const getStrengthColor = () => {
    switch (strengthLevel) {
      case 0:
      case 1: return 'bg-red-400';
      case 2: return 'bg-yellow-400';
      case 3: return 'bg-coral/80';
      case 4:
      case 5: return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getStrengthLabel = () => {
    switch (strengthLevel) {
      case 0:
      case 1: return t('auth.strength.weak');
      case 2: return t('auth.strength.fair');
      case 3: return t('auth.strength.good');
      case 4:
      case 5: return t('auth.strength.strong');
      default: return '';
    }
  };

  useEffect(() => {
    async function check() {
      setValidating(true);
      setError(null);
      try {
        await api.get('/auth/password/validate', { params: { token } });
        setTokenValid(true);
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    }
    if (token) check();
    else { setValidating(false); setTokenValid(false); }
  }, [token]);

  const triggerShake = () => {
    setShakeBtn(true);
    setTimeout(() => setShakeBtn(false), 450);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);

    if (!isPasswordValid) {
      setError(t('auth.error_password_policy'));
      triggerShake();
      return;
    }
    if (pw !== pw2) {
      setError(t('auth.error_password_mismatch'));
      triggerShake();
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password/reset', { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.message || data?.detail || t('auth.error_failed');
      setError(msg);
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const cardShake = { x: [0, -10, 10, -10, 10, 0], transition: { duration: 0.6 } };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        {t('common.loading')}
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md text-center">
          <p className="text-red-500 mb-4">{t('auth.invalid_or_expired_link')}</p>
          <Link to="/forgot" className="text-coral hover:underline">
            {t('auth.forgot_password')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 font-sans px-4">
      <Helmet>
        <title>{t('auth.reset_password')} - Melodis Clinic</title>
      </Helmet>

      <style>
        {`
          @keyframes btn-shake {
            10%, 90% { transform: translateX(-1px); }
            20%, 80% { transform: translateX(2px); }
            30%, 50%, 70% { transform: translateX(-4px); }
            40%, 60% { transform: translateX(4px); }
          }
          .animate-shake { animation: btn-shake 0.45s ease-in-out both; }
        `}
      </style>

      <motion.div
        className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md"
        animate={error ? cardShake : {}}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-coral">
          {t('auth.reset_password')}
        </h2>

        {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
        {done && <p className="text-green-600 mb-4 text-center">{t('auth.reset_success')}</p>}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          {/* New Password */}
          <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
            <FaLock className="text-gray-400 mr-2" />
            <input
              name="new-password"
              type="password"
              placeholder={t('auth.new_password')}
              className="w-full outline-none text-gray-700"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              required
              aria-required="true"
            />
          </div>

          {/* Confirm */}
          <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
            <FaLock className="text-gray-400 mr-2" />
            <input
              name="confirm-password"
              type="password"
              placeholder={t('auth.confirm_password')}
              className="w-full outline-none text-gray-700"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              required
              aria-required="true"
            />
          </div>

          {/* Strength bar + rules（与注册页一致风格） */}
          <div className="mt-2">
            <div className="w-full h-2 bg-gray-200 rounded">
              <div
                className={`h-2 rounded transition-all duration-300 ${getStrengthColor()}`}
                style={{ width: `${(strengthLevel / 5) * 100}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t('auth.strength.label')}: <strong>{getStrengthLabel()}</strong>
            </p>
          </div>
          <div className="mt-3 space-y-1">
            <RuleItem passed={rules.length} label={t('auth.password_rule.length')} />
            <RuleItem passed={rules.uppercase} label={t('auth.password_rule.uppercase')} />
            <RuleItem passed={rules.lowercase} label={t('auth.password_rule.lowercase')} />
            <RuleItem passed={rules.number} label={t('auth.password_rule.number')} />
            <RuleItem passed={rules.special} label={t('auth.password_rule.special')} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-coral text-white py-2 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-60 ${error && shakeBtn ? 'animate-shake' : ''}`}
          >
            {loading ? (t('auth.saving') || t('auth.reset_now')) : t('auth.reset_now')}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const RuleItem: React.FC<{ passed: boolean; label: string }> = ({ passed, label }) => (
  <div className={`flex items-center gap-2 text-sm ${passed ? 'text-green-600' : 'text-gray-500'}`}>
    {passed ? <FaCheckCircle /> : <FaTimesCircle />}{label}
  </div>
);

export default ResetPassword;
